import { formatDateForDisplay, formatMoney, transformIsoDate } from '@/scripts/helper';

// Excess flood attaches over a fixed $100,000 NFIP contents amount, so the contents deductible only means
// something once there is a contents limit to attach it to
const EXCESS_CONTENTS_DEDUCTIBLE = 100000;

// context.contentsLimit is the current Personal Property limit on the form, which the control itself can't see.
// Callers that don't supply it (renewal read-only controls, homeowners endorsements) get the stored value as is.
export function readOnlydisplayValue (control, context = {}) {
    const value = control.value;

    if (control.name === 'ExcessPersonalPropertyDeductible') {
        if (!('contentsLimit' in context)) {
            return formatMoney(value);
        }
        if (!(Number(context.contentsLimit) > 0)) {
            return 'N/A';
        }
        return formatMoney(Number(value) > 0 ? Number(value) : EXCESS_CONTENTS_DEDUCTIBLE);
    }

    if (control.name === 'CovA' ||
        control.name === 'CovB' ||
        control.name === 'CovC' ||
        control.name === 'CovD' ||
        control.name === 'LossAssesmentLimit' ||
        control.name === 'BuildingValuationAmount' ||
        control.name === 'ContentsValuationAmount' ||
        control.name === 'ExcessBuildingDeductible' ||
        control.name === 'WindHailFlatAmount' ||
        control.name === 'WindHailReadOnly' ||
        control.name === 'EarthquakeReadOnly' ||
        control.name === 'FloodReadOnly' ||
        control.name === 'EarthquakeFlatAmount' ||
        control.name === 'WaterReadOnly' ||
        control.name === 'Theft' ||
        control.name === 'WaterBackup' ||
        control.name === 'WaterDamageSublimit' ||
        control.name === 'WaterDamagePeril') {
      return formatMoney(value);
    }

    // value is a string that needs to be an int
    if (control.name === 'AllPerilDeductible') {
      return formatMoney(parseFloat(value));
    }

    if (control.name === 'SquareFeet') {
      return value.toLocaleString();
    }

    if (control.name === 'DateOfBirth' || control.name === 'CoApplicantDateOfBirth' || control.name === 'EffectiveDate' || control.name === 'ExpirationDate') {
      return formatDateForDisplay(value, true);
    }

    if (control.name === 'Protections') {
      return Object.keys(value).filter(key => value[key]).map(val => getControlOptionTextFromControlValue(control, val)).join('<br/>');
    }

    if (control.name === 'OtherRisksAssociated') {
      return otherRisksAssociatedDisplay(value);
    }

    if (control.name === 'AdditionalInterestsList' && control.value) {
      return value.map(x => `${x.Name}, ${x.Type}, ${x.Address.Formatted}`).join('; ');
    }

    if (control.name === 'LossesInPast5YearsList') {
      return value.map(x =>
        `Type: ${x.Type}, Date: ${transformIsoDate(x.Date)}, PaymentAmount: ${formatMoney(x.PaymentAmount)}, IsClaimAtThisLocation: ${x.IsClaimAtThisLocation}, IsClaimClosed: ${x.IsClaimClosed}, IsDamageRepaired: ${x.IsDamageRepaired}, LossDetails: ${x.LossDetails}, MitigateFutureLosses: ${x.MitigateFutureLosses}`)
        .join('; <br /> ');
    }

    if (control.name === 'TheftExclusion' && value === '1') {
      return 'No Coverage';
    }

    if (control.name === 'MailingAddress') {
      return `${value.StreetAddress} ${value.City}, ${value.State} ${value.PostalCode} ${value.CountryCode}`;
    }

    if (control.name === 'RateLock') {
      if (value) {
        return getControlOptionTextFromControlValue(control, value);
      } else {
        return 'No';
      }
    }

    switch (control.type) {
      case 'RadioButtonGroup':
        if (value !== undefined && value !== null) {
          if (value === true) {
            return 'Yes';
          }
          if (value === false) {
            return 'No';
          }
          return value;
        }

        if (control.config.default === true) {
          return 'Yes';
        }
        if (control.config.default === false) {
          return 'No';
        }

        return control.config.default;
      case 'Dropdown':
      case 'RadioItemGroup':
        return getControlOptionTextFromControlValue(control, value);
      case 'MortgageeV1':
        return formatMortgageeV1ForDisplay(value);
      case 'AddressV1':
      case 'Address':
        return value?.Formatted;
      case 'CountryDropdown':
        return value?.CountryCode;
      case 'Checkbox':
        return value === true ? 'Yes' : 'No';
      case 'FormLabel':
        return '';
      default:
        return value?.toString() ?? '';
    }
}
function otherRisksAssociatedDisplay (value) {
    const options = [
      {
        "value": "isCoverageLapse",
        "v2ContractName": "IsCoverageLapse",
        "text": "Dwellings with <b>lapse in coverage?</b>"
      },
      {
        "value": "insuredHighProfileOccupation",
        "v2ContractName": "InsuredHighProfileOccupation",
        "text": "Insured's with <b>high profile occupations?</b>"
      },
      {
        "value": "dwellingWithKnobAndTubeWiring",
        "v2ContractName": "DwellingWithKnobAndTubeWiring",
        "text": "Dwellings with <b>knob and tube</b> wiring, <b>fuses</b>, <b>aluminum or cloth wiring</b>, and/or <b>Federal Pacific Electric (FPE) Stab-Lok</b> breakers (Zinsco Breakers or Electrical Service less than 100 amp)?"
      },
      {
        "value": "noSmokeDetectors",
        "v2ContractName": "NoSmokeDetectors",
        "text": "Dwellings <b>without smoke detectors?</b>"
      },
      {
        "value": "isUnacceptablePlumbing",
        "v2ContractName": "IsUnacceptablePlumbing",
        "text": "Dwellings with <b>plumbing systems</b> containing <b>cast iron/galvanized/lead/polybutylene</b> materials?"
      },
      {
        "value": "existingDamage",
        "v2ContractName": "ExistingDamage",
        "text": "Property with <b>existing damage?</b>"
      },
      {
        "value": "anyMobileHomes",
        "v2ContractName": "AnyMobileHomes",
        "text": "Is this risk a <b>mobile/modular/manufactured home?</b>"
      },
      {
        "value": "fuelTankOnPremise",
        "v2ContractName": "FuelTankOnPremise",
        "text": "Dwellings with a <b>fuel tank</b> on the premise?"
      },
      {
        "value": "dwellingWithMoreThanTwoMortages",
        "v2ContractName": "DwellingWithMoreThanTwoMortages",
        "text": "Dwellings with <b>more than two mortgages?</b>"
      },
      {
        "value": "anyBankruptcyForeclosureRepossession",
        "v2ContractName": "AnyBankruptcyForeclosureRepossession",
        "text": "Any <b>bankruptcy, foreclosure, or repossession</b> in the last 5 years?"
      },
      {
        "value": "isUnacceptableHeatSource",
        "v2ContractName": "IsUnacceptableHeatSource",
        "text": "Dwellings without Central Heating system utilizing <b>Space Heater(s) or Wood Stove(s) as Primary Heat source?</b>"
      },
      {
        "value": "dwellingWithWoodStoveHeat",
        "v2ContractName": "dwellingWithWoodStoveHeat",
        "text": "Dwellings with <b>wood stoves</b> as a utilized heat source?"
      },
      {
        "value": "dwellingsWithDaycareOrAssistedLiving",
        "v2ContractName": "DwellingsWithDaycareOrAssistedLiving",
        "text": "Dwellings with <b>daycare, assisted living operations or student housing?</b>"
      },
      {
        "value": "developersSpeculationHomes",
        "v2ContractName": "DevelopersSpeculationHomes",
        "text": "Developers <b>speculation homes?</b>"
      },
      {
        "value": "dwellingOnNationalRegistery",
        "v2ContractName": "DwellingOnNationalRegistry",
        "text": "Dwellings on the <b>National Registry?</b>"
      },
      {
        "value": "ownFarms",
        "v2ContractName": "OwnFarms",
        "text": "Do you own any <b>farms?</b>"
      },
      {
        "value": "businessOnPremise",
        "v2ContractName": "BusinessOnPremise",
        "text": "Any <b>business</b> on premise?"
      },
      {
        "value": "notInsuredToFullValue",
        "v2ContractName": "NotInsuredToFullValue",
        "text": "Dwellings not insured to <b>100% of RCV value?</b>"
      },
      {
        "value": "isDwellingWithAsbestosOrLead",
        "v2ContractName": "IsDwellingWithAsbestosOrLead",
        "text": "Risks with known <b>asbestos/lead</b> hazard?"
      },
      {
        "value": "isDwellingInCOBRAZone",
        "v2ContractName": "IsDwellingInCOBRAZone",
        "text": "Risks built on <b>Coastal Barrier Land</b> (COBRA Zones)?"
      },
      {
        "value": "isDwellingFallingRocks",
        "v2ContractName": "IsDwellingFallingRocks",
        "text": "Property subject to and/or with <b>prior/current sinkhole activity, landslide, falling rocks, erosion, subsidence or in poor condition?</b>"
      },
      {
        "value": "isDeededToMultipleUnits",
        "v2ContractName": "IsMultipleLivingUnit",
        "text": "Property is <b>deeded</b> to <b>multiple living units?</b>"
      },
      {
        "value": "hasSolarPanelsInstalled",
        "v2ContractName": "IsDwellingWithSolarPanel",
        "text": "Dwellings with <b>Solar Panels</b> installed on the <b>Roof?</b>"
      },
      {
        "value": "isNonStandardConstruction",
        "v2ContractName": "IsDwellingNonStandardConstruction",
        "text": "Dwellings with <b>Non-Standard Construction</b> (i.e., Airplane Hangar, Boat Condo, Fiberglass, Yurts, Tiny/Tree houses, Houseboats, Floating homes, etc.)"
      }
    ];

    const properties = Object.keys(value);
    let returnString = '';
    for (const property of properties) {
      if (value[property]) {
        const option = options.find(o => o.value.toUpperCase() === property.toUpperCase());
        if (option) {
          returnString += option.v2ContractName + '; ';
        }
      }
    }
    return returnString;
}

function getControlOptionTextFromControlValue (control, value) {
    if (value !== undefined) {
      const option = control.config.options.find(option => option.value === value);
      if (option) {
        return option.text;
      }
    }
    return null;
}

function formatMortgageeV1ForDisplay (value) {
    if (value) {
      let formattedMortgageeName = '';

      if (value.MortgageeSuffix) {
        formattedMortgageeName = value.MortgageeName + ' ' +
          value.MortgageeSuffix;
      } else {
        formattedMortgageeName = value.MortgageeName;
      }

      let formattedMortgageeV1 = '<pre>' +
        formattedMortgageeName +
        '\r\n' +
        value.MortgageeAddress1 +
        '\r\n' +
        value.MortgageeCity +
        ', ' + value.MortgageeState +
        ' ' + value.MortgageeZip +
        ' ' + value.MortgageeCountry;

      if (value.MortgageeLoanNumber) {
        formattedMortgageeV1 = formattedMortgageeV1 +
          '\r\n' +
          'Loan Number: ' +
          value.MortgageeLoanNumber +
          '<pre/>';
      }

      if (value.Mortgagee1Invoice != null && value.Mortgagee1Invoice != undefined) {
        formattedMortgageeV1 = formattedMortgageeV1 +
        '\r\n' +
        '<pre>' +
        'Invoice on Renewal: ' +
        (value.Mortgagee1Invoice ? 'Yes' : 'No') +
        '<pre/>';
      }

      return formattedMortgageeV1;
    }

    return "No mortgagee on original policy";
}
